/**
 * Content Tabs Component
 *
 * Manages tabbed content interface similar to accordion but with tab navigation.
 * Each content-tab-row contains a button (tab) and a panel (content).
 */
class ContentTabsInner extends HTMLElement {
  /** @type {NodeListOf<HTMLElement>} */
  get tabButtons() {
    return this.querySelectorAll('.content-tab-row__button');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get tabPanels() {
    return this.querySelectorAll('.content-tab-row__panel');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get tabRows() {
    return this.querySelectorAll('content-tab-row');
  }

  /** @type {number} Current active tab index */
  #currentIndex = 0;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    // Create navigation wrapper
    this.#createNavigation();

    // Set first tab as active
    this.#setActiveTab(0);

    // Add click listeners to all tab buttons
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.#handleTabClick(index), { signal });
    });

    // Add keyboard navigation
    this.addEventListener('keydown', this.#handleKeyDown.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Creates navigation wrapper and moves all tab buttons into it
   */
  #createNavigation() {
    // Check if navigation already exists
    if (this.querySelector('.content-tabs__nav')) return;

    // Create navigation container
    const nav = document.createElement('div');
    nav.className = 'content-tabs__nav';
    nav.setAttribute('role', 'tablist');

    // Move all tab buttons to navigation
    this.tabButtons.forEach((button) => {
      nav.appendChild(button);
    });

    // Insert navigation at the beginning
    this.insertBefore(nav, this.firstChild);
  }

  /**
   * Handle tab click
   * @param {number} index - Index of the clicked tab
   */
  #handleTabClick(index) {
    this.#setActiveTab(index);
  }

  /**
   * Set active tab
   * @param {number} index - Index of the tab to activate
   */
  #setActiveTab(index) {
    if (index < 0 || index >= this.tabButtons.length) return;

    this.#currentIndex = index;

    // Update buttons
    this.tabButtons.forEach((button, i) => {
      const isActive = i === index;
      button.classList.toggle('content-tab-row__button--active', isActive);
      button.setAttribute('aria-selected', isActive.toString());
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update panels
    this.tabPanels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('content-tab-row__panel--active', isActive);

      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('tab-changed', {
        detail: { index },
        bubbles: true,
      })
    );
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    // Only handle keyboard events on tab buttons
    if (!event.target || !(event.target instanceof HTMLElement)) return;
    if (!event.target.classList.contains('content-tab-row__button')) return;

    let newIndex = this.#currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = this.#currentIndex > 0 ? this.#currentIndex - 1 : this.tabButtons.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = this.#currentIndex < this.tabButtons.length - 1 ? this.#currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.tabButtons.length - 1;
        break;
      default:
        return;
    }

    this.#setActiveTab(newIndex);
    const targetButton = this.tabButtons[newIndex];
    if (targetButton) {
      targetButton.focus();
    }
  }
}

if (!customElements.get('content-tabs-inner')) {
  customElements.define('content-tabs-inner', ContentTabsInner);
}

if (!customElements.get('content-tab-row')) {
  customElements.define('content-tab-row', HTMLElement);
}
